import React from 'react';
import { TouchableOpacity, ScrollView, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { ArrowLeft, Shield, Lock, ChevronRight } from 'lucide-react-native';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

// Components
import {
  Screen,
  ScreenHeader,
  SvgBackground,
  ContactEmailItem,
  ContactPhoneItem,
  VerificationStatusItem,
} from '@/components';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';
import { COLORS } from '@/constants/colors';

// API
import { useMyProfile } from '@/api';

type Props = RootStackScreenProps<'ContactDetails'>;

const ContactDetailsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const themeName = useThemeName();
  const isMobileLayout = useIsMobileLayout();
  const isDark = themeName === 'dark';

  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';

  // Fetch user profile
  const { data: profile } = useMyProfile();

  const securityItems = [
    {
      icon: Lock,
      title: t('screens.contactDetails.password'),
      subtitle: t('screens.contactDetails.lastChanged'),
      hasChevron: true,
      onPress: () => {},
    },
    {
      icon: Shield,
      title: t('screens.contactDetails.twoStepVerification'),
      subtitle: t('screens.contactDetails.inactive'),
      action: t('screens.contactDetails.configure'),
      actionColor:
        themeName === 'dark'
          ? 'rgba(249, 115, 22, 0.2)'
          : 'rgba(249, 115, 22, 0.15)',
    },
  ];

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        {Platform.OS === 'web' && !isMobileLayout ? (
          <ScreenHeader
            title={t('screens.contactDetails.title')}
            onBackPress={() => navigation.goBack()}
          />
        ) : (
          <SvgBackground
            offsetLeft={-30}
            offsetTop={-250}
            height={180}
            variant="flat"
            borderBottomLeftRadius={24}
            borderBottomRightRadius={24}
          >
            <XStack
              paddingTop="$10"
              paddingHorizontal="$4"
              paddingBottom="$3"
              alignItems="center"
              space="$3"
              width="100%"
            >
              <TouchableOpacity onPress={() => navigation.goBack()}>
                <ArrowLeft size={24} color="white" />
              </TouchableOpacity>
              <Text
                fontSize={28}
                fontWeight="bold"
                color="white"
                fontFamily="Poppins"
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.8}
                flexShrink={1}
              >
                {t('screens.contactDetails.title')}
              </Text>
            </XStack>
          </SvgBackground>
        )}

        <YStack flex={1}>
          <ScrollView
            contentContainerStyle={{
              paddingTop: 16,
              paddingBottom: 100,
              paddingHorizontal: 16,
            }}
            showsVerticalScrollIndicator={false}
          >
            <YStack space="$6">
              <YStack space="$3">
                <Text
                  fontSize="$5"
                  fontWeight="600"
                  color={themeName === 'dark' ? '#ffffff' : '#18181b'}
                  marginLeft="$2"
                >
                  {t('screens.contactDetails.contactInformation')}
                </Text>

                <YStack space="$3">
                  <ContactEmailItem
                    email={profile?.email || '-'}
                    statusLabel={t('screens.contactDetails.verified')}
                    statusBackgroundColor={
                      themeName === 'dark'
                        ? 'rgba(34, 197, 94, 0.2)'
                        : 'rgba(34, 197, 94, 0.15)'
                    }
                    statusTextColor={
                      themeName === 'dark' ? '#22c55e' : '#15803d'
                    }
                  />
                  <ContactPhoneItem
                    phone={profile?.phoneNumber || '-'}
                    statusLabel={
                      profile?.phoneNumber
                        ? t('screens.contactDetails.verified')
                        : t('screens.contactDetails.notVerified')
                    }
                    statusBackgroundColor={
                      profile?.phoneNumber
                        ? themeName === 'dark'
                          ? 'rgba(34, 197, 94, 0.2)'
                          : 'rgba(34, 197, 94, 0.15)'
                        : themeName === 'dark'
                        ? 'rgba(239, 68, 68, 0.2)'
                        : 'rgba(239, 68, 68, 0.15)'
                    }
                    statusTextColor={
                      profile?.phoneNumber
                        ? themeName === 'dark'
                          ? '#22c55e'
                          : '#15803d'
                        : themeName === 'dark'
                        ? '#ef4444'
                        : '#dc2626'
                    }
                  />
                </YStack>
              </YStack>

              <YStack space="$3">
                <Text
                  fontSize="$5"
                  fontWeight="600"
                  color={themeName === 'dark' ? '#ffffff' : '#18181b'}
                  marginLeft="$2"
                >
                  {t('screens.contactDetails.accountVerification')}
                </Text>

                <VerificationStatusItem
                  title={t('screens.contactDetails.verificationStatus')}
                  subtitle={t(
                    'screens.contactDetails.basicVerificationCompleted',
                  )}
                  actionLabel={t('screens.contactDetails.extend')}
                  actionBackgroundColor={
                    themeName === 'dark'
                      ? 'rgba(249, 115, 22, 0.2)'
                      : 'rgba(249, 115, 22, 0.15)'
                  }
                  actionTextColor={themeName === 'dark' ? '#f97316' : '#c2410c'}
                />
              </YStack>

              <YStack space="$3">
                <Text
                  fontSize="$5"
                  fontWeight="600"
                  color={themeName === 'dark' ? '#ffffff' : '#18181b'}
                  marginLeft="$2"
                >
                  {t('screens.contactDetails.security')}
                </Text>

                <YStack space="$3">
                  {securityItems.map((item, index) => (
                    <YStack
                      key={index}
                      backgroundColor="$gray2"
                      borderRadius="$4"
                      padding="$3"
                      borderWidth={1}
                      borderColor={
                        themeName === 'dark'
                          ? 'rgba(255,255,255,0.15)'
                          : 'rgba(0,0,0,0.08)'
                      }
                    >
                      <TouchableOpacity
                        onPress={item.onPress}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          paddingVertical: 8,
                        }}
                      >
                        <YStack
                          width={40}
                          height={40}
                          borderRadius="$2"
                          backgroundColor="$gray4"
                          borderWidth={1}
                          borderColor={
                            themeName === 'dark'
                              ? 'rgba(255,255,255,0.15)'
                              : 'rgba(0,0,0,0.08)'
                          }
                          alignItems="center"
                          justifyContent="center"
                        >
                          <item.icon
                            size={20}
                            color={themeName === 'dark' ? '#d4d4d8' : '#71717a'}
                          />
                        </YStack>
                        <YStack flex={1} marginLeft={12}>
                          <Text
                            fontSize="$4"
                            fontWeight="500"
                            color={themeName === 'dark' ? '#ffffff' : '#18181b'}
                          >
                            {item.title}
                          </Text>
                          <Text
                            fontSize="$3"
                            color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}
                            marginTop="$1"
                          >
                            {item.subtitle}
                          </Text>
                        </YStack>
                        {item.action && (
                          <YStack
                            backgroundColor={item.actionColor}
                            paddingHorizontal="$3"
                            paddingVertical="$1"
                            borderRadius="$3"
                          >
                            <Text
                              fontSize="$2"
                              fontWeight="500"
                              color={
                                themeName === 'dark' ? '#f97316' : '#c2410c'
                              }
                            >
                              {item.action}
                            </Text>
                          </YStack>
                        )}
                        {item.hasChevron && (
                          <ChevronRight
                            size={16}
                            color={themeName === 'dark' ? '#d4d4d8' : '#71717a'}
                          />
                        )}
                      </TouchableOpacity>
                    </YStack>
                  ))}
                </YStack>
              </YStack>
            </YStack>
          </ScrollView>
        </YStack>
      </YStack>
    </Screen>
  );
};

export default ContactDetailsScreen;
